/**
 * Telas públicas (sem login): criar conta, confirmar e-mail, esqueci a senha, redefinir senha.
 * Usam o mesmo fundo e logo da tela de login.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CnpjLookup, SignupBody } from '@berytus/contracts';
import { api, HttpError } from '../api.ts';
import { LoginLogo } from '../components/Brand.tsx';

function Frame({ title, subtitle, wide, children }: { title: string; subtitle?: ReactNode; wide?: boolean; children: ReactNode }) {
  return (
    <div className="login-wrap">
      <LoginLogo />
      <div className="login" style={wide ? { width: 'min(560px, 100%)' } : undefined}>
        <div>
          <h1>{title}</h1>
          {subtitle && <p className="muted small" style={{ margin: '4px 0 0' }}>{subtitle}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Algo deu errado. Tente de novo.');

/** Máscara de CNPJ que aceita letras (CNPJ alfanumérico). */
const maskCnpj = (v: string) => {
  const c = v.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 14);
  return [c.slice(0, 2), c.slice(2, 5), c.slice(5, 8)].filter(Boolean).join('.') + (c.length > 8 ? `/${c.slice(8, 12)}` : '') + (c.length > 12 ? `-${c.slice(12)}` : '');
};

export function Signup() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [f, setF] = useState({ cnpj: '', legalName: '', tradeName: '', vertical: 'pharmacy' as SignupBody['vertical'], name: '', email: '', phone: '', password: '', acceptTerms: false });
  const [cnpjInfo, setCnpjInfo] = useState<{ tone: 'ok' | 'err' | 'muted'; text: string } | null>(null);
  const set = (k: keyof typeof f, v: string | boolean) => setF((x) => ({ ...x, [k]: v }));

  const lookup = async () => {
    const digits = f.cnpj.replace(/[^0-9A-Z]/gi, '');
    if (digits.length !== 14) return;
    try {
      const r = await api<CnpjLookup>(`/signup/cnpj/${digits}`);
      setF((x) => ({ ...x, legalName: x.legalName || r.legalName || '', tradeName: x.tradeName || r.tradeName || r.legalName || '' }));
      setCnpjInfo(r.legalName
        ? { tone: r.active === false ? 'err' : 'ok', text: `${r.legalName}${r.city ? ` · ${r.city}/${r.state}` : ''}${r.active === false ? ' · situação não ativa na Receita' : ''}` }
        : { tone: 'muted', text: 'CNPJ válido. Preencha os dados da empresa.' });
    } catch (err) {
      setCnpjInfo({ tone: 'err', text: errText(err) });
    }
  };

  const signup = useMutation({
    mutationFn: () => api('/signup', { method: 'POST', json: { ...f, phone: f.phone || undefined } }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['me'] });
      navigate('/', { replace: true });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    signup.mutate();
  };

  return (
    <Frame title="Criar conta grátis" subtitle="30 dias para testar: até 10 produtos e 10 anúncios por marketplace, sem cartão." wide>
      <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
        <div className="field">
          <label htmlFor="cnpj">CNPJ</label>
          <input className="input" id="cnpj" required autoFocus inputMode="text" placeholder="00.000.000/0000-00" value={f.cnpj}
            onChange={(e) => { set('cnpj', maskCnpj(e.target.value)); setCnpjInfo(null); }} onBlur={lookup} />
          {cnpjInfo && <span className="small" style={{ color: cnpjInfo.tone === 'err' ? 'var(--err)' : cnpjInfo.tone === 'ok' ? 'var(--ok)' : 'var(--muted)' }}>{cnpjInfo.text}</span>}
        </div>
        <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div className="field"><label htmlFor="legal">Razão social</label><input className="input" id="legal" required value={f.legalName} onChange={(e) => set('legalName', e.target.value)} /></div>
          <div className="field"><label htmlFor="trade">Nome fantasia</label><input className="input" id="trade" required value={f.tradeName} onChange={(e) => set('tradeName', e.target.value)} /></div>
        </div>
        <div className="field">
          <label>Segmento</label>
          <div className="seg" role="radiogroup" style={{ alignSelf: 'flex-start' }}>
            <button type="button" aria-pressed={f.vertical === 'pharmacy'} onClick={() => set('vertical', 'pharmacy')}>Farmácia</button>
            <button type="button" aria-pressed={f.vertical === 'general'} onClick={() => set('vertical', 'general')}>Outro e-commerce</button>
          </div>
        </div>
        <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div className="field"><label htmlFor="name">Seu nome</label><input className="input" id="name" required autoComplete="name" value={f.name} onChange={(e) => set('name', e.target.value)} /></div>
          <div className="field"><label htmlFor="phone">Telefone</label><input className="input" id="phone" autoComplete="tel" inputMode="tel" value={f.phone} onChange={(e) => set('phone', e.target.value)} /></div>
        </div>
        <div className="field"><label htmlFor="email">E-mail</label><input className="input" id="email" type="email" required autoComplete="email" value={f.email} onChange={(e) => set('email', e.target.value)} /></div>
        <div className="field">
          <label htmlFor="password">Senha</label>
          <input className="input" id="password" type="password" required minLength={10} autoComplete="new-password" value={f.password} onChange={(e) => set('password', e.target.value)} />
          <span className="small muted">Pelo menos 10 caracteres.</span>
        </div>
        <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <input type="checkbox" className="check" required checked={f.acceptTerms} onChange={(e) => set('acceptTerms', e.target.checked)} />
          <span>Li e aceito os termos de uso e a <a href="/privacidade" target="_blank" rel="noreferrer">política de privacidade</a> da Berytus.</span>
        </label>
        {signup.error && <div className="error" role="alert">{errText(signup.error)}</div>}
        <button className="btn primary" type="submit" disabled={signup.isPending}>{signup.isPending ? 'Criando a conta…' : 'Criar conta e começar o teste'}</button>
        <p className="small muted" style={{ margin: 0, textAlign: 'center' }}>Já tem conta? <Link to="/login">Entrar</Link></p>
      </form>
    </Frame>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const qc = useQueryClient();
  const sent = useRef(false);
  const verify = useMutation({
    mutationFn: () => api('/auth/verify-email', { method: 'POST', json: { token } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
  useEffect(() => {
    if (token && !sent.current) {
      sent.current = true;
      verify.mutate();
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Frame title="Confirmar e-mail">
      {!token ? <p style={{ margin: 0 }}>Link incompleto. Abra o link do e-mail de novo.</p>
        : verify.isPending || verify.isIdle ? <p style={{ margin: 0 }}>Confirmando…</p>
        : verify.error ? <div className="error">{errText(verify.error)}</div>
        : <p style={{ margin: 0 }}>E-mail confirmado. Agora você pode conectar os marketplaces.</p>}
      <Link className="btn primary" to="/">Ir para o painel</Link>
    </Frame>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const forgot = useMutation({ mutationFn: () => api('/auth/password/forgot', { method: 'POST', json: { email } }) });
  return (
    <Frame title="Esqueci minha senha" subtitle="Enviamos um link para criar uma nova senha.">
      {forgot.isSuccess ? (
        <p style={{ margin: 0 }}>Se houver uma conta com <b>{email}</b>, o link chega em instantes. Ele vale 1 hora. Confira também a caixa de spam.</p>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); forgot.mutate(); }} style={{ display: 'grid', gap: 12 }}>
          <div className="field"><label htmlFor="email">E-mail</label><input className="input" id="email" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          {forgot.error && <div className="error">{errText(forgot.error)}</div>}
          <button className="btn primary" type="submit" disabled={forgot.isPending}>Enviar link</button>
        </form>
      )}
      <p className="small muted" style={{ margin: 0, textAlign: 'center' }}><Link to="/login">Voltar para o login</Link></p>
    </Frame>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const reset = useMutation({ mutationFn: () => api('/auth/password/reset', { method: 'POST', json: { token: params.get('token'), password } }) });
  const mismatch = confirm.length > 0 && confirm !== password;
  const invite = params.get('convite') === '1';
  return (
    <Frame title={invite ? 'Bem-vindo! Crie sua senha' : 'Criar nova senha'}>
      {reset.isSuccess ? (
        <>
          <p style={{ margin: 0 }}>{invite ? 'Senha criada. Entre com o seu e-mail e a senha nova.' : 'Senha alterada. Por segurança, as sessões abertas foram encerradas.'}</p>
          <button className="btn primary" type="button" onClick={() => navigate('/login')}>Entrar</button>
        </>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); if (!mismatch) reset.mutate(); }} style={{ display: 'grid', gap: 12 }}>
          {invite && <p className="small muted" style={{ margin: 0 }}>Você foi convidado para a Berytus Conecta. A senha precisa ter pelo menos 10 caracteres.</p>}
          <div className="field"><label htmlFor="p1">{invite ? 'Senha' : 'Nova senha'}</label><input className="input" id="p1" type="password" required minLength={10} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
          <div className="field">
            <label htmlFor="p2">Repita a senha</label>
            <input className="input" id="p2" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            {mismatch && <span className="small" style={{ color: 'var(--err)' }}>As senhas não conferem.</span>}
          </div>
          {reset.error && <div className="error">{reset.error instanceof HttpError && reset.error.code === 'invalid_token' ? <>{invite ? 'Convite vencido ou já usado: peça um novo a quem convidou você, ou use ' : 'Link vencido. '}<Link to="/esqueci-senha">{invite ? 'Esqueci a senha' : 'Peça outro'}</Link>.</> : errText(reset.error)}</div>}
          <button className="btn primary" type="submit" disabled={reset.isPending || mismatch}>{invite ? 'Criar senha' : 'Salvar nova senha'}</button>
        </form>
      )}
    </Frame>
  );
}
